// @ts-check
/** WhatsApp copy for an order's CURRENT payment_status/order_status - the client-side half of
 * the same "always current status, never a free choice" design backend/routes/admin/orders.py's
 * send-payment-status-email/send-fulfillment-status-email implement for email. Each function
 * returns the message text for whichever status `order` is actually in right now, or null when
 * that status has no customer message (not_applicable, pending - too early). To send a different
 * message, the real status has to change first (Record a Payment, or Update Order Status). */

const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

/** "Your Ivory Silk Dress" for one piece, "Your pieces" for several, "Your order" for none -
 * never a doubled "your your ...". Mirrors backend/services/email_service.py's piece_subject.
 * @param {Array<{product_name?: string}>} items
 */
function pieceSubject(items) {
  const names = (items || []).map((i) => i.product_name).filter(Boolean);
  if (names.length === 1) return `Your ${names[0]}`;
  if (names.length > 1) return 'Your pieces';
  return 'Your order';
}

/** @param {{payment_status?: string, order_number?: string, shipping_first_name?: string, total?: number, amount_paid?: number, balance_due?: number, items?: Array<{product_name?: string}>, id?: string}} order
 * @param {string} paymentLink - already resolved (e.g. via posOrder.js's paymentUrl)
 * @returns {string | null}
 */
export function paymentStatusWhatsAppMessage(order, paymentLink) {
  const name = order.shipping_first_name || 'there';
  const piece = pieceSubject(order.items);

  switch (order.payment_status) {
    case 'awaiting_payment':
      return `Hi ${name}, here is your secure KAAY payment link for ${inr(order.total)}:\n${paymentLink}`;
    case 'partially_paid':
      return [
        `Hi ${name}, thank you — we've received ${inr(order.amount_paid)} towards ${piece.toLowerCase()}.`,
        `The remaining balance of ${inr(order.balance_due)} can be settled whenever suits you best: ${paymentLink}`,
        '',
        'Thank you for choosing KAAY.',
      ].join('\n');
    case 'paid':
      return `Hi ${name}, thank you for shopping with KAAY! Order ${order.order_number} is confirmed - total ${inr(order.total)}.`;
    case 'failed':
      return `Hi ${name}, your recent payment attempt for order ${order.order_number} wasn't successful - no amount was charged. You can try again here: ${paymentLink}`;
    case 'expired':
      return `Hi ${name}, the reservation for order ${order.order_number} has expired and the items were released. Do let us know if you'd like to reorder.`;
    case 'refunded':
      return `Hi ${name}, a refund of ${inr(order.total)} has been issued for order ${order.order_number}.`;
    default:
      return null; // not_applicable, or any legacy value with no defined message
  }
}

/** @param {{order_status?: string, order_number?: string, shipping_first_name?: string, tracking_number?: string, carrier?: string, tracking_url?: string}} order
 * @returns {string | null}
 */
export function fulfillmentStatusWhatsAppMessage(order) {
  const name = order.shipping_first_name || 'there';
  const num = order.order_number;

  switch (order.order_status) {
    case 'confirmed':
      return `Hi ${name}, your order ${num} is confirmed and being carefully prepared.`;
    case 'processing':
      return `Hi ${name}, your order ${num} is in our hands and being prepared for dispatch.`;
    case 'shipped':
      return order.tracking_number
        ? `Hi ${name}, your order ${num} has shipped via ${order.carrier || 'courier'}! Tracking: ${order.tracking_number}${order.tracking_url ? `\n${order.tracking_url}` : ''}`
        : `Hi ${name}, your order ${num} is on its way! We'll share tracking details shortly.`;
    case 'delivered':
      return `Hi ${name}, your order ${num} has been delivered. We hope you love your new piece!`;
    case 'cancelled':
      return `Hi ${name}, your order ${num} has been cancelled. If you didn't expect this or have any questions, just let us know.`;
    default:
      return null; // pending - too early for a customer message
  }
}
